import React, { useCallback, useMemo } from 'react'
import useGetOnboardingWholesalers, {
  useValidateWholesalerCredentialsMutation
} from 'app/auth/components/hooks/use-get-filtered-onboarding-wholesalers'
import * as routing from '../../constants/routes'
import { useHistory } from 'react-router-dom'
import { AdditionalWholesaler } from './types.d'
import { useTranslation } from 'react-i18next'
import { BaseStepLayout } from '../BaseStepLayout'
import { useOnboardingContext } from 'app/auth/components/onboarding/use-onboarding-context'
import { OrderAccountFlow } from 'app/settings/components/OrderAccounts/components/OrderAccountFlow'
import { generateApiError } from 'utils/Request/Error'
import { FormProps } from 'app/settings/components/OrderAccounts/components/OrderAccountCredentialsDialogContent'
import { useNotifier } from '@cbgms/base/notifier'
import { Stack } from '@mui/material'
import { Button, LoadingButton } from '@carsys/ui'
import { useFormikContext } from 'formik'

const useGetWholesalers = () => {
  const { onboardingInfo } = useOnboardingContext()
  const isPortalOnboarding = onboardingInfo.shop ? onboardingInfo.shop.Data.ModulePortalEnabled : true
  return useGetOnboardingWholesalers(isPortalOnboarding)
}

const SubmitButton: React.FC<{ loading: boolean }> = ({ loading }) => {
  const { submitForm } = useFormikContext()
  const { t } = useTranslation()

  return (
    <LoadingButton
      disabled={loading}
      loading={loading}
      onClick={submitForm}
      color='primary'
      variant='contained'
      data-testid='validate-supplier-credentials-btn'
    >
      {t('Configuration.ValidateSave')}
    </LoadingButton>
  )
}

export const AdditionalWholesalers: React.FC<{ addAdditionalWholesaler: (a: AdditionalWholesaler) => void; basePath: string }> = ({
  addAdditionalWholesaler,
  basePath
}) => {
  const notifier = useNotifier()
  const validateMutation = useValidateWholesalerCredentialsMutation()
  const { t } = useTranslation()
  const h = useHistory()
  const onSubmit = useCallback(
    async values => {
      try {
        const result = await validateMutation.mutateAsync(values)
        return { ...values, ...result }
      } catch (e: any) {
        const apiError = generateApiError(e)
        notifier.error(apiError.errorMessage())
        throw new Error(apiError.errorMessage())
      }
    },
    [validateMutation.mutate]
  )
  const onSubmitSuccess = useCallback(
    async (values: FormProps) => {
      addAdditionalWholesaler({ ...values })
      h.push(routing.urlSetUpOrderAccounts(basePath))
    },
    [h?.push]
  )

  const onCancel = useCallback(() => h.push(routing.urlSetUpOrderAccounts(basePath)), [h.push, basePath])

  const cancelButton = useMemo(
    () => (
      <Stack direction='row' spacing={1}>
        <Button variant='outlined' onClick={onCancel}>
          {t('global.back')}
        </Button>
      </Stack>
    ),
    [onCancel]
  )

  const addOrderAccountButtons = useMemo(
    () => (
      <Stack direction='row' spacing={1}>
        <Button variant='outlined' onClick={onCancel}>
          {t('global.back')}
        </Button>
        <SubmitButton loading={validateMutation.isLoading} />
      </Stack>
    ),
    [onCancel, validateMutation.isLoading]
  )

  return (
    <BaseStepLayout title={t('onboarding.order_accounts.order_accounts_header')} subTitles={[]}>
      <OrderAccountFlow
        useGetWholesalers={useGetWholesalers}
        onClose={onCancel}
        onSubmit={onSubmit}
        onSubmitSuccess={onSubmitSuccess}
        initialAccountUUID='unused'
        initialWholesalerUUID={undefined}
        initialOrderAccountUUID={undefined}
        initialCredentials={undefined}
        initialDoNotShowOnList={undefined}
        forceHideDoNotShowOnList
        cancelWholesalerSelectButtonSlot={cancelButton}
        addOrderAccountButtonSlot={addOrderAccountButtons}
      />
    </BaseStepLayout>
  )
}

export default AdditionalWholesalers
